import { NavLink, Navigate, Outlet, useLocation, useParams } from 'react-router';
import {
  PLANNED_CAPABILITY_NOTE,
  usableOrgCapabilities,
  type OrgCapability,
} from '@gatecheckpro/contracts';
import { Status, presentation, VERIFICATION_STATES } from '@gatecheckpro/design-system';
import { useSession, useViewer } from '../lib/session.tsx';
import { ErrorBoundary } from '../lib/ErrorBoundary.tsx';

/**
 * The organization workspace shell.
 *
 * Navigation is built from the viewer's *live* capabilities, so the workspace
 * offers exactly what the API will honour. Capabilities that exist in the role
 * table but not yet in the product are listed separately and honestly, with the
 * slice that brings them — "arriving in Slice 2" is a far better answer than a
 * greyed-out button with no explanation (kickoff Sections 7, 17).
 */
interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly capability: OrgCapability;
  readonly end?: boolean;
}

const NAV: readonly NavItem[] = [
  { to: '', label: 'Overview', capability: 'ORG_VIEW', end: true },
  { to: 'events', label: 'Events', capability: 'EVENT_MANAGE' },
  { to: 'venues', label: 'Venues', capability: 'EVENT_MANAGE' },
  { to: 'team', label: 'Team', capability: 'ORG_VIEW' },
  { to: 'verification', label: 'Verification', capability: 'ORG_VIEW' },
  { to: 'activity', label: 'Activity', capability: 'ORG_VIEW' },
  // FINANCE_VIEW rather than FINANCE_APPROVE: reading the position and opening a
  // refund is the viewer's capability. What an approver may additionally do is
  // decided on the page, and finally by the server.
  { to: 'finance', label: 'Finance', capability: 'FINANCE_VIEW' },
];

/** What the organizer holds but the product cannot yet do. Ordered as they ship. */
const PLANNED_ORDER: readonly OrgCapability[] = [
  'GATE_OPERATE',
  'GATE_SUPERVISE',
  'FINANCE_VIEW',
  'FINANCE_APPROVE',
  'SUPPORT_ACT',
];

export function WorkspaceLayout() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const location = useLocation();
  const viewer = useViewer();
  const { signOut } = useSession();

  const organization = viewer.organizations.find(
    (candidate) => candidate.organizationId === organizationId,
  );

  // An organization the viewer has no standing in is simply not in their list.
  // Sending them back to the picker matches what the API would say.
  if (organization === undefined) return <Navigate to="/organizations" replace />;

  const capabilities = usableOrgCapabilities(organization.capabilities as OrgCapability[], {
    mfaSatisfied: viewer.mfa.satisfiedForSession,
  });
  const verification = presentation(VERIFICATION_STATES, organization.verificationState);
  const planned = PLANNED_ORDER.filter((capability) => PLANNED_CAPABILITY_NOTE[capability]);

  return (
    <div className="gc-app">
      <header className="gc-header">
        <div className="gc-container gc-header__inner">
          <NavLink to="/organizations" className="gc-wordmark">
            GateCheck<span className="gc-wordmark__accent">Pro</span>
            <span className="gc-wordmark__suffix">Organizer</span>
          </NavLink>

          <nav className="gc-nav" aria-label="Workspace">
            {NAV.filter((item) => capabilities.includes(item.capability)).map((item) => (
              <NavLink
                key={item.label}
                to={item.to}
                end={item.end === true}
                className="gc-nav__link"
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="gc-row" style={{ marginInlineStart: 'auto' }}>
            <NavLink to="/security" className="gc-nav__link">
              Security
            </NavLink>
            <button className="gc-button gc-button--quiet" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="gc-main">
        <div className="gc-container gc-stack gc-stack--loose">
          <div className="gc-page-header">
            <span className="gc-eyebrow">Organization</span>
            <div className="gc-row gc-row--baseline">
              <h1>{organization.name}</h1>
              <Status
                tone={verification.tone}
                glyph={verification.glyph}
                label={verification.label}
                title={verification.meaning}
              />
            </div>
            <p className="gc-muted">
              {organization.slug} · signed in as {viewer.displayName}
            </p>
          </div>

          {/*
            Keyed on the path, so navigating away is a real recovery route
            rather than a way to carry a failed boundary onto the next page.
            The header and navigation sit outside it deliberately: an operator
            who can still reach another page has a working product, and one
            staring at a blank document does not.
          */}
          <ErrorBoundary key={location.pathname} what="This page">
            <Outlet />
          </ErrorBoundary>

          {planned.length > 0 && (
            <section className="gc-stack gc-stack--tight">
              <h3>Not in this release</h3>
              <p className="gc-muted">
                These are part of GateCheckPro and are not built yet. Nothing here is hidden behind
                a permission you already hold.
              </p>
              <ul className="gc-stack gc-stack--tight" style={{ paddingInlineStart: '1.1rem' }}>
                {planned.map((capability) => (
                  <li key={capability} className="gc-muted">
                    {PLANNED_CAPABILITY_NOTE[capability]}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </main>

      <footer className="gc-footer">
        {/*
          Deliberately says nothing about which release this is. The previous
          wording named Slice 1 and was still saying so at Slice 9B, because a
          release fact written as a literal in a layout has nothing keeping it
          true. Internal sequencing is not customer-facing truth; what the
          workspace can and cannot do is stated by the capability list above,
          which is derived rather than typed out.
        */}
        <div className="gc-container">GateCheckPro by CABS · Organizer workspace</div>
      </footer>
    </div>
  );
}
